/**
 * Utility for managing referral codes in localStorage with expiration
 * 
 * Referral codes expire after 24 hours to ensure they're only valid
 * for a short period after the user clicks the referral link.
 */

const REFERRAL_CODE_KEY = 'referralCode';
const EXPIRATION_TIME_MS = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

/**
 * Stores a referral code in localStorage with timestamp
 * @param {string} code - The referral code to store
 */
export const setReferralCode = (code) => {
  if (!code) return;

  const data = {
    code: code.trim(),
    timestamp: Date.now(),
    expiresAt: Date.now() + EXPIRATION_TIME_MS
  };

  try {
    localStorage.setItem(REFERRAL_CODE_KEY, JSON.stringify(data));
    console.log('Referral code stored:', {
      code: code.trim(),
      expiresAt: new Date(data.expiresAt).toISOString()
    });
  } catch (error) {
    console.error('Failed to store referral code:', error);
  }
};

/**
 * Retrieves the referral code if it exists and hasn't expired
 * @returns {string|null} - The referral code or null if expired/not found
 */
export const getReferralCode = () => {
  try {
    const storedData = localStorage.getItem(REFERRAL_CODE_KEY);
    
    if (!storedData) {
      return null;
    }

    const data = JSON.parse(storedData);
    const now = Date.now();

    // Check if the code has expired
    if (now > data.expiresAt) {
      console.log('Referral code expired, removing from storage');
      clearReferralCode();
      return null;
    }

    // Code is still valid
    const remainingTime = Math.floor((data.expiresAt - now) / 1000);
    console.log(`Referral code is valid. Expires in ${remainingTime} seconds`);
    
    return data.code;
  } catch (error) {
    console.error('Failed to retrieve referral code:', error);
    clearReferralCode(); // Clear corrupted data
    return null;
  }
};

/**
 * Removes the referral code from localStorage
 */
export const clearReferralCode = () => {
  try {
    localStorage.removeItem(REFERRAL_CODE_KEY);
    console.log('Referral code cleared from storage');
  } catch (error) {
    console.error('Failed to clear referral code:', error);
  }
};

/**
 * Checks if a referral code exists and is still valid
 * @returns {boolean} - True if valid code exists, false otherwise
 */
export const hasValidReferralCode = () => {
  return getReferralCode() !== null;
};
